import { z } from 'zod';

export const OPTIMIZE_ROUTE_TYPES = [
  'pickup',
  'delivery',
  'pickup-delivery',
] as const;

export const DEFAULT_COLORS = [
  '#32CD32',
  '#4ECDC4',
  '#FF6B6B',
  '#FFD166',
  '#1A535C',
  '#2EC4B6',
  '#F4A261',
];

const hexColorRegex = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

const precedencePerTypeSchema = z
  .object({
    pickup: z
      .number({ invalid_type_error: 'La precedencia debe ser numérica' })
      .int('La precedencia debe ser un número entero')
      .min(0, 'La precedencia debe ser mayor o igual a 0')
      .optional(),
    delivery: z
      .number({ invalid_type_error: 'La precedencia debe ser numérica' })
      .int('La precedencia debe ser un número entero')
      .min(0, 'La precedencia debe ser mayor o igual a 0')
      .optional(),
    'pickup-delivery': z
      .number({ invalid_type_error: 'La precedencia debe ser numérica' })
      .int('La precedencia debe ser un número entero')
      .min(0, 'La precedencia debe ser mayor o igual a 0')
      .optional(),
  })
  .partial();

const colorsPerTypeSchema = z
  .object({
    pickup: z
      .string()
      .regex(hexColorRegex, 'El color debe estar en formato hexadecimal')
      .optional(),
    delivery: z
      .string()
      .regex(hexColorRegex, 'El color debe estar en formato hexadecimal')
      .optional(),
    'pickup-delivery': z
      .string()
      .regex(hexColorRegex, 'El color debe estar en formato hexadecimal')
      .optional(),
  })
  .partial();

export const optimizeRouteSchema = z
  .object({
    dividePoint: z.boolean({
      required_error: 'Divide point es requerido',
    }),
    type: z
      .enum(OPTIMIZE_ROUTE_TYPES, {
        errorMap: () => ({ message: 'Tipo de optimización inválido' }),
      })
      .optional(),
    precedence: z
      .number({ invalid_type_error: 'La precedencia debe ser numérica' })
      .int('La precedencia debe ser un número entero')
      .min(0, 'La precedencia debe ser mayor o igual a 0')
      .optional(),
    color: z
      .string()
      .regex(hexColorRegex, 'El color debe estar en formato hexadecimal')
      .optional(),
    precedencePerType: precedencePerTypeSchema.optional(),
    colors: colorsPerTypeSchema.optional(),
  })
  .passthrough()
  .superRefine((data, ctx) => {
    if (data.dividePoint) {
      const hasPrecedencePerType =
        !!data.precedencePerType &&
        Object.values(data.precedencePerType).some(
          (value) => value !== undefined
        );
      if (!hasPrecedencePerType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['precedencePerType'],
          message:
            'Debe definir la precedencia por tipo cuando divide en múltiples puntos',
        });
      }

      const hasColors =
        !!data.colors &&
        Object.values(data.colors).some(
          (value) => value !== undefined && value !== ''
        );
      if (!hasColors) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['colors'],
          message:
            'Debe definir los colores por tipo cuando divide en múltiples puntos',
        });
      }
    } else {
      if (!data.type) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['type'],
          message: 'El tipo de optimización es requerido',
        });
      }
      if (data.precedence === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['precedence'],
          message: 'La precedencia es requerida',
        });
      }
      if (!data.color) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['color'],
          message: 'El color es requerido',
        });
      }
    }
  });

export const serviceRuleSchema = z
  .object({
    signatureRequired: z.boolean({
      required_error: 'Debe indicar si la firma es requerida',
    }),
    labelTemplate: z
      .string()
      .min(1, 'El template no puede estar vacío')
      .optional(),
    labelTemplatePattern: z
      .string()
      .min(1, 'El patrón no puede estar vacío')
      .optional(),
    color: z
      .string()
      .regex(hexColorRegex, 'El color debe estar en formato hexadecimal')
      .optional(),
    precedence: z
      .number({ invalid_type_error: 'La precedencia debe ser numérica' })
      .int('La precedencia debe ser un número entero')
      .min(0, 'La precedencia debe ser mayor o igual a 0')
      .optional(),
    dividePoint: z.boolean().optional(),
    notes: z.string().optional(),
  })
  .passthrough();

export const rulesConfigSchema = z
  .object({
    strategy: z
      .object({
        optimizeRoute: optimizeRouteSchema,
      })
      .passthrough(),
    services: z
      .record(serviceRuleSchema)
      .refine((value) => Object.keys(value).length > 0, {
        message: 'Debe configurar al menos un servicio',
      }),
  })
  .passthrough();

export type OptimizeRouteType = (typeof OPTIMIZE_ROUTE_TYPES)[number];

export type OptimizeRouteConfig = z.infer<typeof optimizeRouteSchema>;

export type ServiceRuleConfig = z.infer<typeof serviceRuleSchema>;

export type RulesConfig = z.infer<typeof rulesConfigSchema>;

export interface RulesConfigResponse {
  id: string;
  rulesConfig: RulesConfig | null;
}

export interface UpdateRulesConfigPayload {
  rulesConfig: RulesConfig | null;
}

export const DEFAULT_RULES_CONFIG: RulesConfig = {
  strategy: {
    optimizeRoute: {
      dividePoint: false,
      type: 'delivery',
      precedence: 0,
      color: '#32CD32',
    },
  },
  services: {
    delivery: {
      signatureRequired: false,
    },
  },
};
